/* ==========================================================================
   MyAthanClock — Product Page JS
   Wires up gallery, variant selection, cart, FAQ, sticky bar.
   ========================================================================== */

(function(){
  'use strict';

  // Update cart counter / icon WITHOUT opening drawer
  function refreshCartUI(cart){
    // 1) Text-based cart counts
    document.querySelectorAll('[data-cart-count], .cart-count, [data-cart-bubble-count], #cart-count').forEach(el => {
      el.textContent = cart.item_count;
    });

    // 2) Dispatch events modern themes listen for
    ['cart:refresh','cart:update','cart:updated','cart:added','cart:item-added','cart:change']
      .forEach(name => document.dispatchEvent(new CustomEvent(name, { detail: { cart }, bubbles: true })));

    // 3) Invoke refresh methods on Horizon-style web components
    document.querySelectorAll('cart-icon, cart-bubble, cart-drawer-component, cart-items-component').forEach(el => {
      ['refresh','update','reload','render','refreshFromCart'].forEach(m => {
        if (typeof el[m] === 'function'){ try { el[m](cart); } catch(e){} }
      });
    });

    // 4) Section Rendering API — try common section names that contain cart elements
    const candidateSections = ['header_section','header','cart-drawer','cart-icon-bubble','cart-notification'];
    fetch(window.location.pathname + '?sections=' + candidateSections.join(','), { headers: { 'Accept':'application/json' } })
      .then(r => r.ok ? r.json() : null)
      .then(sections => {
        if (!sections) return;
        // Combine all returned section HTML into one doc to search
        const combined = Object.values(sections).filter(Boolean).join('\n');
        if (!combined) return;
        const parsed = new DOMParser().parseFromString(combined, 'text/html');
        const swapSelectors = [
          'cart-drawer-component',
          'cart-items-component',
          'cart-icon',
          'cart-bubble',
          '.cart-count',
          '[data-cart-count]',
          '[data-cart-bubble-count]'
        ];
        swapSelectors.forEach(sel => {
          const newEl = parsed.querySelector(sel);
          const currentEl = document.querySelector(sel);
          if (newEl && currentEl){
            // Swap inner content — preserves the custom element's identity & event listeners
            currentEl.innerHTML = newEl.innerHTML;
            // Notify any listeners on the component
            currentEl.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart } }));
          }
        });
      })
      .catch(() => {});
  }

  document.querySelectorAll('.myac-section').forEach(initSection);

  function initSection(section){
    const dataEl = section.querySelector('[data-myac-product]');
    if (!dataEl) return;

    let product, options;
    try {
      product = JSON.parse(dataEl.textContent);
    } catch(e){ console.error('myac: invalid product JSON', e); return; }

    options = product.options || [];

    const state = {
      images: product.media || product.images || [],
      currentImg: 0,
      qty: 1,
      // selectedOptions[i] = chosen value for option i (e.g. ['Birch Beige', 'Cross-stitch'])
      selectedOptions: product.variants[0].options.slice(),
      currentVariant: product.variants[0]
    };

    /* ---------- Gallery ---------- */
    const galleryImg = section.querySelector('[data-myac-gallery-img]');
    const galleryIx = section.querySelector('[data-myac-gallery-ix]');
    const thumbsWrap = section.querySelector('[data-myac-thumbs]');

    function renderThumbs(){
      if (!thumbsWrap) return;
      thumbsWrap.innerHTML = '';
      state.images.forEach((img, i) => {
        const t = document.createElement('div');
        t.className = 'myac-thumb' + (i === state.currentImg ? ' active' : '');
        const src = imageUrl(img, 180);
        t.innerHTML = `<img src="${src}" alt=""/>`;
        t.addEventListener('click', () => setImage(i));
        thumbsWrap.appendChild(t);
      });
    }
    function setImage(i){
      const len = state.images.length;
      if (!len) return;
      state.currentImg = ((i % len) + len) % len;
      const img = state.images[state.currentImg];
      if (galleryImg){
        galleryImg.src = imageUrl(img, 1600);
        galleryImg.alt = img.alt || product.title;
      }
      if (galleryIx) galleryIx.textContent = `${state.currentImg + 1} / ${len}`;
      if (thumbsWrap){
        [...thumbsWrap.children].forEach((t, idx) => t.classList.toggle('active', idx === state.currentImg));
      }
    }
    function imageUrl(img, width){
      // img can be a Shopify image object or a URL string
      const src = typeof img === 'string' ? img : (img.src || img.preview_image?.src || '');
      if (!src) return '';
      return src.replace(/(\?|&)width=\d+/, '').replace(/(\.jpg|\.jpeg|\.png|\.webp)/i, m => m) + (src.includes('?') ? '&' : '?') + 'width=' + width;
    }

    renderThumbs();
    setImage(0);

    /* ---------- Gallery arrows ---------- */
    section.querySelector('[data-myac-prev]')?.addEventListener('click', () => setImage(state.currentImg - 1));
    section.querySelector('[data-myac-next]')?.addEventListener('click', () => setImage(state.currentImg + 1));

    /* ---------- Variant selection ---------- */
    section.querySelectorAll('[data-myac-option]').forEach(group => {
      const optIdx = parseInt(group.dataset.myacOption, 10);
      group.querySelectorAll('[data-value]').forEach(btn => {
        btn.addEventListener('click', () => {
          state.selectedOptions[optIdx] = btn.dataset.value;
          group.querySelectorAll('[data-value]').forEach(b => b.classList.toggle('active', b === btn));
          syncOptionLabel(section, optIdx, btn.dataset.value);
          updateVariant();
        });
      });
    });

    function syncOptionLabel(scope, idx, value){
      const label = scope.querySelector(`[data-myac-option-label="${idx}"]`);
      if (label) label.textContent = value;
    }

    function updateVariant(){
      const match = product.variants.find(v =>
        v.options.every((o, i) => o === state.selectedOptions[i])
      );
      if (!match) return;
      state.currentVariant = match;

      // Update hidden variant id input so native form submit (and Shop Pay) work
      section.querySelectorAll('[data-myac-variant-input]').forEach(el => { el.value = match.id; });

      // Update price displays
      section.querySelectorAll('[data-myac-price]').forEach(el => {
        el.innerHTML = formatMoney(match.price);
      });
      section.querySelectorAll('[data-myac-compare]').forEach(el => {
        if (match.compare_at_price && match.compare_at_price > match.price){
          el.innerHTML = formatMoney(match.compare_at_price);
          el.style.display = '';
        } else {
          el.style.display = 'none';
        }
      });
      section.querySelectorAll('[data-myac-save]').forEach(el => {
        if (match.compare_at_price && match.compare_at_price > match.price){
          const save = match.compare_at_price - match.price;
          el.innerHTML = 'Save ' + formatMoney(save);
          el.style.display = '';
        } else {
          el.style.display = 'none';
        }
      });

      // Swap gallery to variant image if it has one
      if (match.featured_image){
        const idx = state.images.findIndex(img => {
          const id = typeof img === 'string' ? img : (img.id || img.src);
          const target = match.featured_image.id || match.featured_image.src;
          return id === target || (typeof img !== 'string' && img.src === match.featured_image.src);
        });
        if (idx >= 0) setImage(idx);
      }

      // Update URL with ?variant=ID
      const url = new URL(window.location);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url);

      // Update sold-out / availability
      const ctaText = section.querySelector('[data-myac-cta-text]');
      const ctaBtn = section.querySelector('[data-myac-add-to-cart]');
      if (ctaBtn && ctaText){
        if (match.available){
          ctaBtn.disabled = false;
          ctaBtn.classList.remove('disabled');
          ctaText.textContent = 'Add to cart';
        } else {
          ctaBtn.disabled = true;
          ctaBtn.classList.add('disabled');
          ctaText.textContent = 'Sold out';
        }
      }
      updateQtyTotal();
    }

    function formatMoney(cents){
      // Cents to dollars. For real Shopify, use the shop's money_format.
      const dollars = (cents / 100).toFixed(2);
      return '$' + dollars;
    }

    /* ---------- Quantity ---------- */
    const qtyInput = section.querySelector('[data-myac-qty-input]');
    section.querySelector('[data-myac-qty-minus]')?.addEventListener('click', () => {
      state.qty = Math.max(1, state.qty - 1);
      if (qtyInput) qtyInput.value = state.qty;
      updateQtyTotal();
    });
    section.querySelector('[data-myac-qty-plus]')?.addEventListener('click', () => {
      state.qty = Math.min(99, state.qty + 1);
      if (qtyInput) qtyInput.value = state.qty;
      updateQtyTotal();
    });

    function updateQtyTotal(){
      const totalEl = section.querySelector('[data-myac-cta-total]');
      if (totalEl){
        totalEl.textContent = '— ' + formatMoney(state.currentVariant.price * state.qty);
      }
    }

    /* ---------- Add to cart ---------- */
    section.querySelectorAll('[data-myac-add-to-cart]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        addToCart(btn, state.currentVariant.id, state.qty);
      });
    });

    function addToCart(btn, variantId, quantity){
      const originalHTML = btn.innerHTML;
      btn.disabled = true;
      btn.classList.add('disabled');
      btn.innerHTML = '<span>Adding…</span>';

      // Horizon cart pattern: collect cart-items-component section IDs so /cart/add.js
      // returns fresh section HTML in response.sections
      const sectionIds = [];
      document.querySelectorAll('cart-items-component').forEach(el => {
        if (el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
      });

      const body = { id: variantId, quantity: quantity };
      if (sectionIds.length){
        body.sections = sectionIds.join(',');
        body.sections_url = window.location.pathname;
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(body)
      })
      .then(r => r.json())
      .then(response => {
        // Native Horizon event — cart-items-component listens for this and morphs its
        // own innerHTML from event.detail.data.sections[its-section-id]
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: null,
            sourceId: 'myac-product-form',
            data: {
              source: 'product-form-component',
              itemCount: quantity,
              sections: response.sections || {}
            }
          }
        }));
        return fetch('/cart.js').then(r => r.json());
      })
      .then(cart => {
        refreshCartUI(cart);
        btn.innerHTML = '<span style="color:#a8d49a">✓ Added</span>';

        setTimeout(() => {
          btn.innerHTML = originalHTML;
          btn.disabled = false;
          btn.classList.remove('disabled');
        }, 1800);
      })
      .catch(err => {
        console.error('cart add failed', err);
        btn.innerHTML = '<span style="color:#c44">Error — try again</span>';
        setTimeout(() => {
          btn.innerHTML = originalHTML;
          btn.disabled = false;
          btn.classList.remove('disabled');
        }, 2000);
      });
    }

    /* ---------- Bundle CTA ---------- */
    section.querySelectorAll('[data-myac-bundle-add]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const ids = (btn.dataset.variants || '').split(',').filter(Boolean).map(s => ({ id: parseInt(s.trim(), 10), quantity: 1 }));
        if (!ids.length) { console.warn('myac: no bundle variant IDs set'); return; }
        const originalHTML = btn.innerHTML;
        btn.disabled = true;
        btn.classList.add('disabled');
        btn.innerHTML = '<span>Adding bundle…</span>';
        // Horizon cart pattern — collect section IDs and include in request
        const bundleSectionIds = [];
        document.querySelectorAll('cart-items-component').forEach(el => {
          if (el.dataset.sectionId) bundleSectionIds.push(el.dataset.sectionId);
        });
        const bundleBody = { items: ids };
        if (bundleSectionIds.length){
          bundleBody.sections = bundleSectionIds.join(',');
          bundleBody.sections_url = window.location.pathname;
        }
        fetch('/cart/add.js', {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify(bundleBody)
        })
        .then(r => r.json())
        .then(response => {
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: null,
              sourceId: 'myac-bundle',
              data: {
                source: 'product-form-component',
                itemCount: ids.reduce((s,i) => s + (i.quantity||1), 0),
                sections: response.sections || {}
              }
            }
          }));
          return fetch('/cart.js').then(r => r.json());
        })
        .then(cart => {
          refreshCartUI(cart);
          btn.innerHTML = '<span style="color:#a8d49a">✓ Bundle added</span>';
          setTimeout(() => {
            btn.innerHTML = originalHTML;
            btn.disabled = false;
            btn.classList.remove('disabled');
          }, 2000);
        })
        .catch(err => {
          console.error('bundle add failed', err);
          btn.innerHTML = '<span style="color:#c44">Error — try again</span>';
          setTimeout(() => {
            btn.innerHTML = originalHTML;
            btn.disabled = false;
            btn.classList.remove('disabled');
          }, 2000);
        });
      });
    });

    /* ---------- FAQ accordion ---------- */
    section.querySelectorAll('.myac-faq-item').forEach(item => {
      const q = item.querySelector('.myac-faq-q');
      if (!q) return;
      q.addEventListener('click', () => {
        const open = item.classList.contains('open');
        section.querySelectorAll('.myac-faq-item').forEach(i => i.classList.remove('open'));
        if (!open) item.classList.add('open');
      });
    });

    /* ---------- Color cards (mat) jumping to swatches ---------- */
    section.querySelectorAll('[data-myac-color-card]').forEach(card => {
      card.addEventListener('click', () => {
        const val = card.dataset.myacColorCard;
        const target = section.querySelector(`[data-myac-option="0"] [data-value="${val}"]`);
        if (target) target.click();
        section.querySelector('[data-myac-hero]')?.scrollIntoView({ behavior: 'smooth' });
      });
    });

    /* ---------- Sticky bar ---------- */
    const stickybar = section.querySelector('[data-myac-stickybar]');
    const heroEl = section.querySelector('[data-myac-hero]');
    const closingEl = section.querySelector('[data-myac-closing]');
    function onScroll(){
      if (!stickybar || !heroEl) return;
      const heroBottom = heroEl.getBoundingClientRect().bottom;
      const closingTop = closingEl ? closingEl.getBoundingClientRect().top : Infinity;
      const show = heroBottom < 60 && closingTop > window.innerHeight + 100;
      stickybar.classList.toggle('show', show);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    // Initialize price/availability
    updateVariant();
  }
})();